"use client";

import { useEffect, useCallback, useState } from "react";
import { useNavigate, useLocation, useMatches, Outlet, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "@speakai/ui/sidebar";
import { Suspense } from "react";
import { lazyWithReload } from "@/lib/utils/lazy-with-reload";
import { MomentBeacon } from "@/features/moments/moment-beacon";
import { useAuth } from "@/hooks/use-auth";
import { useQuickActionShortcuts } from "@/hooks/use-quick-action-shortcuts";
import { useEmailClickCapture } from "@/features/analytics/use-email-click-capture";
import { useSeedAssistantPreference } from "@/features/chat/use-seed-assistant-preference";
import { useUnsavedTakeNotice } from "@/features/recorder/hooks/use-unsaved-take-notice";
import { AppSidebar } from "@/components/layout/sidebar";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { PageTransition } from "@/components/shared/page-transition";
import { Menu, X } from "lucide-react";
import { SidePanel } from "@speakai/ui/side-panel";
import { useMediaQuery } from "@/hooks/use-media-query";
import { DevCohortSwitcher } from "@/components/dev/dev-cohort-switcher";
import { SubscriptionGateDialog } from "@/features/subscription/components/subscription-gate-dialog";
import { GlobalCaptureDialog } from "@/features/dashboard/components/global-capture-dialog";
import { GlobalAddBalance } from "@/features/subscription/components/global-add-balance";
import { Link } from "react-router-dom";
import { TopBar } from "@/components/layout/top-bar";
import { MeetingReminderHost } from "@/features/meeting-reminders/meeting-reminder-host";
import { LaunchAnnouncementBar } from "@/features/launch/components/launch-announcement-bar";
import { useNewTopBar } from "@/config/constants";
import { SuggestChangePanel } from "@/features/improvements/components/suggest-change-panel";

// CsvImportDialog — lazy-loaded so it doesn't bloat the dashboard bundle.
// Opens when the ?dialog=import-csv search param is present, overlaying the
// current page without navigation (same pattern as join_meeting dialog).
const CsvImportDialog = lazyWithReload(() => import("@/features/csv-import/components/csv-import-dialog"), "CsvImportDialog");

const SpotlightSearch = lazyWithReload(() => import("@/components/shared/spotlight-search"), "SpotlightSearch");
const AgenticPanel = lazyWithReload(() => import("@/features/chat/components/agentic-panel"), "AgenticPanel");
const GlobalExitIntentDialog = lazyWithReload(() => import("@/features/exit-intent/components/global-exit-intent-dialog"), "GlobalExitIntentDialog");
const ChatFab = lazyWithReload(() => import("@/features/chat/components/chat-fab"), "ChatFab");

export function DashboardLayout({ fullBleed: fullBleedProp }: { fullBleed?: boolean }) {
  return (
    <SidebarProvider>
      <DashboardContent fullBleedProp={fullBleedProp} />
    </SidebarProvider>
  );
}

function DashboardContent({ fullBleedProp }: { fullBleedProp?: boolean }) {
  // Records an email landing when the URL carries utm_source=speakmail. Mounted here
  // rather than in app.tsx because it needs an authenticated user to attribute to.
  useEmailClickCapture();
  // On the shell, not the chat dialog: automation-only users never open chat.
  useSeedAssistantPreference();
  const navigate = useNavigate();
  const location = useLocation();
  const matches = useMatches();
  const { isLoggedIn, isLoading, user } = useAuth();
  useUnsavedTakeNotice(user?._id);
  const { t } = useTranslation();
  const { collapsed, mobileOpen, setMobileOpen } = useSidebar();
  const isDesktopNav = useMediaQuery("(min-width: 768px)");
  const mobileMenuOpen = mobileOpen && !isDesktopNav;
  // The panel mounts a render after `open` flips, so focus waits for its node.
  const [mobileMenuPanel, setMobileMenuPanel] = useState<HTMLDivElement | null>(null);
  const closeMobileMenu = useCallback(() => setMobileOpen(false), [setMobileOpen]);

  useEffect(() => {
    if (isDesktopNav && mobileOpen) setMobileOpen(false);
  }, [isDesktopNav, mobileOpen, setMobileOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen) mobileMenuPanel?.querySelector<HTMLElement>("[data-testid='mobile-menu-close']")?.focus();
  }, [mobileMenuOpen, mobileMenuPanel]);

  const [searchParams, setSearchParams] = useSearchParams();
  const csvImportOpen = searchParams.get("dialog") === "import-csv";
  const handleCsvImportClose = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("dialog");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useQuickActionShortcuts({
    onScheduleMeeting: () => navigate("/meeting-assistant?dialog=schedule"),
  });

  // Full-bleed: either passed as prop (pathless sibling) or marked on the matched route handle
  const isFullBleed = fullBleedProp ?? matches.some((m) => (m.handle as { fullBleed?: boolean } | null)?.fullBleed);

  const isAgentsRoute = location.pathname.startsWith("/agents");

  const userName = user?.firstName || undefined;
  const userEmail = user?.email || undefined;
  const userAvatar = user?.picture;

  const needsOnboarding = Boolean(user?.isNewUser && !user.onboarding?.goal);

  useEffect(() => {
    if (!isLoading && !isLoggedIn) {
      const { pathname, search } = location;
      const returnUrl = `${pathname}${search}`;
      navigate(`/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`, { replace: true });
    }
  }, [isLoading, isLoggedIn, location, navigate]);

  useEffect(() => {
    if (isLoading || !isLoggedIn) return;
    if (needsOnboarding) {
      navigate("/survey", { replace: true });
    }
  }, [isLoading, isLoggedIn, needsOnboarding, navigate]);

  if (isLoading || !isLoggedIn || needsOnboarding) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        {t("ACTION.SKIP_TO_CONTENT")}
      </a>

      <div className="flex flex-col h-[100dvh] bg-background">
        <LaunchAnnouncementBar />
        {useNewTopBar ? (
          <TopBar />
        ) : (
          <header className="md:hidden fixed top-0 left-0 right-0 z-30 h-14 bg-card border-b border-border flex items-center px-4 gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label={t("ACTION.OPEN_MENU")}
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/home" className="inline-flex">
              <img src="/logo.png" alt="Speak AI" className="h-6 w-auto object-contain dark:invert" />
            </Link>
          </header>
        )}

        <div className="flex flex-1 min-h-0">
          <SidePanel
            ref={setMobileMenuPanel}
            open={mobileMenuOpen}
            onClose={closeMobileMenu}
            side="left"
            title={t("MENU.NAVIGATION_MENU", "Navigation menu")}
            header={
              <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-3">
                <span className="px-1 text-xl font-bold tracking-tight text-foreground">Speak AI</span>
                <button
                  type="button"
                  onClick={closeMobileMenu}
                  aria-label={t("ACTION.CLOSE")}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="mobile-menu-close"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            }
            className="w-[280px] sm:w-[280px] h-[100dvh] overflow-hidden md:hidden"
          >
            <div className="min-h-0 flex-1">
              <AppSidebar
                userName={userName}
                userEmail={userEmail}
                userAvatar={userAvatar}
              />
            </div>
          </SidePanel>

          <div
            className={`hidden md:flex md:flex-col shrink-0 transition-all duration-300 ease-in-out ${
              collapsed ? "w-[60px]" : "w-[240px]"
            }`}
          >
            <AppSidebar
              userName={userName}
              userEmail={userEmail}
              userAvatar={userAvatar}
            />
          </div>

          <div className={`${useNewTopBar ? "" : "pt-14 md:pt-0"} flex-1 flex flex-col overflow-hidden min-w-0`}>
            <main id="main-content" className="flex-1 overflow-y-auto overflow-x-clip">
              {isFullBleed ? (
                <ErrorBoundary>
                  <PageTransition className="h-full">
                    <Outlet />
                  </PageTransition>
                </ErrorBoundary>
              ) : (
                <div className="pt-6 pb-24">
                  <div className="px-4 sm:px-6 md:px-8">
                    <ErrorBoundary>
                      <PageTransition>
                        <Outlet />
                      </PageTransition>
                    </ErrorBoundary>
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>

      {!isAgentsRoute && (
        <>
          <Suspense><AgenticPanel /></Suspense>
          {!mobileMenuOpen && <Suspense><ChatFab /></Suspense>}
        </>
      )}
      <Suspense><SpotlightSearch /></Suspense>
      <DevCohortSwitcher />
      <SubscriptionGateDialog />
      <Suspense fallback={null}>
        <GlobalCaptureDialog />
      </Suspense>
      <GlobalAddBalance />
      <Suspense fallback={null}>
        <CsvImportDialog open={csvImportOpen} onClose={handleCsvImportClose} />
      </Suspense>
      <Suspense fallback={null}>
        <GlobalExitIntentDialog />
      </Suspense>
      <MomentBeacon />
      <MeetingReminderHost />
      <SuggestChangePanel />
    </>
  );
}
